import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import PropTypes from 'prop-types';
import { graphql, navigate } from 'gatsby';
import styled from '@emotion/styled';

import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { containerStyles } from '../../styles/layout';
import { body, headers, richTextStyles } from '../../styles/type';

import AnchorElement from '../AnchorElement';
import IconCard from '../IconCard';
import TabMenu from '../TabMenu';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  overflow: hidden;
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(56)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Header = styled.div`
  text-align: center;

  & > :last-child {
    margin-bottom: ${rem(64)};
  }
`;

const Headline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};
`;

const Subhead = styled.h2`
  ${headers.h6}
  color: ${colors.gray['600']};
  margin-top: ${rem(20)};
`;

const TabsWrapper = styled.div`
`;

const TabPanel = styled.div`
  overflow: hidden;
  height: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity 200ms ease-in;

  ${({ show }) => show && `
    overflow: visible;
    height: auto;
    opacity: 1;
    visibility: visible;
  `}
`;

const Grid = styled.div`
  display: grid;
  gap: ${rem(40)};
  grid-template-columns: repeat(auto-fit, minmax(${rem(300)}, 1fr));

  ${({ totalItems }) => totalItems < 3 && `
    justify-content: center;
    margin: 0 auto;
    max-width: ${totalItems === 2 ? rem(1000) : rem(480)};
  `}

  ${({ marginBottom }) => marginBottom && `
    margin-bottom: ${rem(55)};
  `}
`;

const TextContent = styled.div`
  ${body.regular}
  ${richTextStyles}
`;

const Tabs = ({
  headline,
  subhead,
  tabCategories,
  backgroundColor,
  tabStyle,
  showMobileDropdown,
  mobileDropdownLabel
}) => {
  const tabNames = tabCategories.map((tabCategory) => tabCategory.headline);
  const ariaControlIds = tabCategories.map((tabCategory, index) => (
    `tab-${tabCategory.id}-${index}`
  ));
  const anchorIds = tabCategories.map((tabCategory) => (
    tabCategory.anchorId || tabCategory.id
  ));
  const [activeIndex, setActiveIndex] = useState(0);
  const { hash, pathname, search } = useLocation();

  const changeTab = (index) => {
    navigate(
      `${pathname}${search}#${anchorIds[index]}`,
      { state: { disableScroll: true } }
    );
  };

  useEffect(() => {
    const hashIndex = anchorIds.indexOf(hash.replace('#', ''));
    if (hashIndex !== -1) {
      setActiveIndex(hashIndex);
    }
  }, [hash, anchorIds]);

  return (
    <>
      {anchorIds.length > 1 && anchorIds.map((anchorId) => (
        <AnchorElement key={anchorId} id={anchorId} />
      ))}
      <Container backgroundColor={backgroundColor}>
        <ContentWrapper>
          <Header>
            {headline && (
              <Headline>
                <TextRenderer text={headline} />
              </Headline>
            )}
            {subhead && (
              <Subhead>
                <TextRenderer text={subhead} />
              </Subhead>
            )}
          </Header>
          <TabsWrapper>
            <TabMenu
              tabStyle={tabStyle}
              tabNames={tabNames}
              activeIndex={activeIndex}
              onChange={changeTab}
              ariaControlIds={ariaControlIds}
              mobileDropdownLabel={mobileDropdownLabel}
              showMobileDropdown={showMobileDropdown}
            />
            {tabCategories?.map((tabCategory, index) => (
              <TabPanel
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                show={index === activeIndex}
                role="tabpanel"
                id={ariaControlIds[index]}
                tabIndex={0}
              >
                {tabCategory.iconCards && tabCategory.iconCards.length > 0 && (
                  <Grid
                    totalItems={tabCategory.iconCards.length}
                    marginBottom={tabCategory.text !== null}
                  >
                    {tabCategory.iconCards.map((iconCard, cardIndex) => (
                      <IconCard
                        // eslint-disable-next-line react/no-array-index-key
                        key={cardIndex}
                        image={iconCard.image}
                        headline={iconCard.headline}
                        text={iconCard.text}
                        ctaText={iconCard.ctaText}
                        ctaLink={iconCard.ctaLink}
                        secondaryCtaText={iconCard.secondaryCtaText}
                        secondaryCtaLink={iconCard.secondaryCtaLink}
                      />
                    ))}
                  </Grid>
                )}
                {tabCategory.text && (
                  <TextContent>
                    <TextRenderer text={tabCategory.text} richText />
                  </TextContent>
                )}
              </TabPanel>
            ))}
          </TabsWrapper>
        </ContentWrapper>
      </Container>
    </>
  );
};

Tabs.propTypes = {
  headline: PropTypes.string,
  subhead: PropTypes.string,
  tabCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      headline: PropTypes.string,
      iconCards: PropTypes.arrayOf(PropTypes.shape(IconCard.propTypes)),
      text: PropTypes.shape({
        raw: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        references: PropTypes.arrayOf(PropTypes.object)
      })
    })
  ),
  backgroundColor: PropTypes.oneOf(['Blue', 'White']),
  tabStyle: PropTypes.oneOf(['Button', 'Underline'])
};

export const query = graphql`
  fragment TabsFragment on ContentfulTabs {
    id
    __typename
    headline
    subhead
    tabCategories {
      id
      headline
      anchorId
      iconCards {
        ...IconCardFragment
      }
      text {
        raw
        references {
          ...TextRendererAssetFragment
          ...TextRendererButtonFragment
          ...TextRendererFormFragment
          ...TextRendererTrialFormFragment
          ...TextRendererImageFragment
          ...TextRendererMediaFragment
          ...TextRendererVideoFragment
          ...TextRendererWidgetFragment
        }
      }
    }
    backgroundColor
    tabStyle
    showMobileDropdown
    mobileDropdownLabel
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Tabs;
